import type { ConfigProps } from '../../configTypes';
import executeGraphQlRequest from './executeGraphQlRequest';
import { formatHighlightFieldTitle } from '../lib/formatHighlightFieldTitle';
import { partitionContributorSocialMedia } from '../lib/partitionContributorSocialMedia';
import {
  resolveRoles,
  resolveHighlightSnippet,
  resolveContributorImage,
  type ContributorNodeBase,
} from '../lib/transformContributorNode';
import type { ContributorSocialMedia } from '../types/contributorProfile';

export type ContributorCard = {
  id: string;
  name: string;
  slug: string;
  uri: string;
  roles: string[];
  imageSrc?: string;
  imageAlt?: string;
  /** WP repeater `contributors_career` — hub card subtitle when set */
  careerDescription?: string;
  /** First hub snippet from CMS highlight fields, with display label */
  highlight?: { label: string; text: string };
};

type ContributorNode = ContributorNodeBase & {
  id: string;
  socialMedia?: ContributorSocialMedia[];
};

const contributorNodeFields = `
  id
  databaseId
  title
  slug
  uri
  contributorsCategories { nodes { name } }
  highlightFields { type items }
  featuredImage { node { altText publicId sourceUrl } }
  socialMedia { key value }
`;

function toContributorCard(node: ContributorNode): ContributorCard {
  const snippet = resolveHighlightSnippet(node.highlightFields);
  const { careerDescription } = partitionContributorSocialMedia(
    node.socialMedia,
  );
  return {
    id: String(node.databaseId),
    name: node.title ?? '',
    slug: node.slug,
    uri: node.uri,
    roles: resolveRoles(node),
    imageSrc: resolveContributorImage(node),
    imageAlt: node.featuredImage?.node?.altText ?? node.title ?? '',
    careerDescription,
    highlight: snippet
      ? {
          label: formatHighlightFieldTitle(snippet.type),
          text: snippet.text,
        }
      : undefined,
  };
}

/**
 * Fetches contributors filtered by IDs, a category slug, or latest.
 *
 * - `ids` provided  → `contributors(where: { in: $ids })`, then sliced to `limit`
 * - `category` provided → `contributorsCategory(id: $slug, idType: SLUG) { contributors { ... } }`
 * - neither → `contributors(first: $limit)` (most recently added)
 *
 * `limit` is respected in all three modes (defaults to 1 000 when omitted).
 *
 * Returns `ContributorCard[]` — same shape used by the contributor hub.
 *
 * **Note:** The `category` path requires the `contributorsCategory` WPGraphQL root field
 * (registered when the `contributors_category` taxonomy is exposed via WPGraphQL).
 * If your schema uses a different field name, update the query accordingly.
 */
export default async function fetchContributorsByCategory(
  config: ConfigProps,
  options: {
    ids?: string[];
    category?: string;
    limit?: number;
  } = {},
): Promise<ContributorCard[]> {
  const { ids, category, limit = 1000 } = options;
  const cmsUrl = config.cmsUrl;

  try {
    if (ids?.length) {
      const query = `
        query ContributorsByIds($ids: [ID!]!) {
          contributors(where: { in: $ids }) {
            nodes { ${contributorNodeFields} }
          }
        }
      `;
      const data = await executeGraphQlRequest<{
        contributors: { nodes: ContributorNode[] };
      }>({ cmsUrl, query, variables: { ids } });
      const results = (data?.contributors?.nodes ?? []).map(toContributorCard);
      return limit ? results.slice(0, limit) : results;
    }

    if (category) {
      const query = `
        query ContributorsByCategory($category: ID!, $limit: Int!) {
          contributorsCategory(id: $category, idType: SLUG) {
            contributors(first: $limit) {
              nodes { ${contributorNodeFields} }
            }
          }
        }
      `;
      const data = await executeGraphQlRequest<{
        contributorsCategory: {
          contributors: { nodes: ContributorNode[] };
        } | null;
      }>({ cmsUrl, query, variables: { category, limit } });
      return (data?.contributorsCategory?.contributors?.nodes ?? []).map(
        toContributorCard,
      );
    }

    // Latest — no filter
    const query = `
      query LatestContributors($limit: Int!) {
        contributors(first: $limit) {
          nodes { ${contributorNodeFields} }
        }
      }
    `;
    const data = await executeGraphQlRequest<{
      contributors: { nodes: ContributorNode[] };
    }>({ cmsUrl, query, variables: { limit } });
    return (data?.contributors?.nodes ?? []).map(toContributorCard);
  } catch (err) {
    console.error('[fetchContributorsByCategory] GraphQL request failed', err);
    return [];
  }
}
